import {ChangeEvent, InputHTMLAttributes, useState} from 'react';

type DecimalInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'defaultValue' | 'onChange'> & {
    value: number | null | undefined;
    onValueChange: (value: number) => void;
};

/** Zero shows as an empty field, so the placeholder shows. */
const toText = (value: number | null | undefined): string => (value ? String(value) : '');

/**
 * OWA-406: a number input that keeps the text being typed and reports the number. Binding a
 * number input straight to a number re-renders it from the parsed value on every keystroke:
 * deleting the 5 from 0.5 parses as 0, which renders as an empty field, so the user had to
 * retype the whole value. A new value from outside (another tab, a reset) still replaces the
 * text, unless the text already means that value.
 */
const DecimalInput = ({value, onValueChange, ...inputProps}: DecimalInputProps) => {
    const [text, setText] = useState(() => toText(value));
    const [reported, setReported] = useState(value);

    if (value !== reported) {
        setReported(value);
        if (Number(text) !== (value ?? 0)) setText(toText(value));
    }

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        // A number input reads as '' while its text is not yet a number (for example "."), and
        // the browser keeps showing what was typed
        const next = Number(e.target.value);
        setText(e.target.value);
        if (!Number.isFinite(next)) return;
        setReported(next);
        onValueChange(next);
    };

    return <input {...inputProps} type="number" value={text} onChange={onChange}/>;
};

export default DecimalInput;
